/* eslint-disable import/no-extraneous-dependencies */
import { Grid, Typography } from '@mui/material';
import { SubmitButton } from '@tassat/button';
import { SecondaryApiTypeEnum, SecondaryApiTypeMap } from '@tassat/common-definitions';
import { SelectField, SimpleErrorMessage, TextField } from '@tassat/formik-input';
import { SelectOption } from '@tassat/ui-definitions';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validatePairApiConnections } from './submissions';
import {
    IFormProps,
    IFormValues,
    IFormikProps
} from './types';

// TODO Move to common/constants file
const apiConnectionBlurb = { [SecondaryApiTypeEnum.FIREBLOCKS]: 'Press submit to generate a new API Key and Secret for the Fireblocks API' };

const FormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        submitForm,
        isSubmitting,
        isValid,
        submitError,
        features,
        userApiTypes,
        showBlurb,
        user
    } = props;

    const isSecondaryConnectionsAllowed = features?.fireblocks.api;
    const isSecondaryConnectionSelected = values.ApiConnection?.value !== '0';

    const apiOptions = React.useMemo((): SelectOption[] => {
        if (isSecondaryConnectionsAllowed) {
            let opts = [] as SelectOption[];
            if (userApiTypes?.length) {
                opts = userApiTypes.reduce((acc, api) => {
                    if (SecondaryApiTypeMap[api.APISecondaryConnectionType]) {
                        acc = [
                            ...acc,
                            {
                                label: SecondaryApiTypeMap[api.APISecondaryConnectionType].displayValue,
                                value: SecondaryApiTypeMap[api.APISecondaryConnectionType].value
                            }
                        ];
                    }

                    return acc;
                }, [] as SelectOption[]);
            }

            if (user.IsTokenAPIUser) {
                opts.push({
                    label: 'Token API',
                    value: '0'
                });
            }

            return opts;
        }

        return [];
    }, [ userApiTypes, isSecondaryConnectionsAllowed, user.IsTokenAPIUser ]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                alignItems='center'
                container
                direction='column'
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
            >
                {isSecondaryConnectionsAllowed ? (
                    <Grid
                        className={classes.fullWidth}
                        item
                        md={6}
                        sm={12}
                    >
                        <SelectField
                            errors={{}}
                            label='API Connection'
                            name='ApiConnection'
                            options={apiOptions}
                            placeholder='Select API Connection Type'
                            values={values}
                        />
                    </Grid>
                ) : null}
                {showBlurb && isSecondaryConnectionSelected && !errors.ApiConnection ? (
                    <Typography className={classes.blurbText}>{apiConnectionBlurb[values.ApiConnection?.value as string]}</Typography>
                ) : null}
                {
                    errors.ApiConnection ?
                        (<Typography className={`${classes.blurbText} ${classes.blurbErrorText}`}>{errors.ApiConnection}</Typography>)
                        : null
                }
                {

                    !isSecondaryConnectionsAllowed ||
                    (isSecondaryConnectionsAllowed && !isSecondaryConnectionSelected) ?
                        (
                            <Grid
                                className={classes.fullWidth}
                                item
                                md={6}
                                sm={12}
                            >
                                <TextField
                                    errors={errors}
                                    label='API Client ID'
                                    name='apiClientId'
                                    placeholder='Enter API Code...'
                                    values={values}
                                />
                            </Grid>
                        ) : null
                }
                <SubmitButton
                    disabled={isSubmitting || !isValid}
                    onClick={submitForm}
                    type='submit'
                />
                <SimpleErrorMessage
                    error={submitError}
                />
            </Grid>
        </form>
    );
};

export function PairApiConnectionForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        user,
        apiConnections,
        apiConnectionUsers,
        submitPairApiConnection,
        setUserCredentials,
        showBlurb
    } = formProps;

    const initialValues: IFormValues = { apiClientId: '' };

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const pairApiConnectionOriginalValidations = (values: IFormValues) => validatePairApiConnections(
        values, initialValues, {
            userId: user.UserId,
            apiConnections,
            apiConnectionUsers
        }
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: IFormValues) => {
                try {
                    await submitPairApiConnection(values, {
                        onClose,
                        setSubmitError,
                        setUserCredentials,
                        user,
                        apiConnectionUsers
                    });
                } catch (error) {
                    setSubmitError('Failed to pair API');
                }
            }}
            validate={pairApiConnectionOriginalValidations}
            validateOnChange
            validateOnMount
        >
            {
                (formContentProps: FormikProps<IFormValues>) => (
                    <FormContents
                        {...formContentProps}
                        classes={classes}
                        features={formProps.features}
                        formProps={formProps}
                        showBlurb={showBlurb}
                        submitError={submitError}
                        user={formProps.user}
                        userApiTypes={formProps.userApiTypes}
                    />
                )
            }
        </Formik>
    );
}